import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface FormattedContentProps {
  content: string;
  searchQuery?: string;
}

export const FormattedContent: React.FC<FormattedContentProps> = ({ content, searchQuery }) => {
  if (!content) return null;

  const highlightText = (text: string, query: string) => {
    if (!query || query.trim().length < 2) return text;
    
    // Escape query regex characters
    const escapedQuery = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark 
          key={index} 
          className="bg-[var(--t-warning)]/30 text-white rounded px-0.5 search-match border border-[var(--t-warning)]/50 transition-all duration-200"
        >
          {part}
        </mark>
      ) : part
    );
  };

  const highlightChildren = (node: React.ReactNode, query: string): React.ReactNode => {
    if (!query || query.trim().length < 2) return node;
    if (typeof node === 'string') {
      return highlightText(node, query);
    }
    if (React.isValidElement(node)) {
      if (node.type === 'mark' && (node.props as any).className?.includes('search-match')) {
        return node;
      }
      if (node.props && node.props.children) {
        return React.cloneElement(node, {
          ...node.props,
          children: React.Children.map(node.props.children, c => highlightChildren(c, query))
        } as any);
      }
    }
    if (Array.isArray(node)) {
      return node.map((item, index) => <React.Fragment key={index}>{highlightChildren(item, query)}</React.Fragment>);
    }
    return node;
  };

  return (
    <div className="flex flex-col gap-1.5 overflow-hidden prose prose-invert max-w-none">
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <div className="text-[11px] font-black uppercase tracking-[0.1em] text-[var(--t-primary)] mt-3 mb-1 flex items-center gap-2">
              <span className="w-1 h-3 bg-[var(--t-primary)] rounded-full" />
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </div>
          ),
          h2: ({ children }) => (
            <div className="text-[10px] font-black uppercase tracking-[0.1em] text-[var(--t-primary)] mt-3 mb-1 flex items-center gap-2">
              <span className="w-1 h-3 bg-[var(--t-primary)] rounded-full" />
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </div>
          ),
          h3: ({ children }) => (
            <div className="text-[9px] font-black uppercase tracking-[0.1em] text-[var(--t-primary)] mt-2 mb-1 flex items-center gap-2">
              <span className="w-1 h-2 bg-[var(--t-primary)] rounded-full" />
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </div>
          ),
          blockquote: ({ children }) => (
            <div className="pl-3 border-l-2 border-[var(--t-primary)]/30 italic text-[var(--t-text-muted)] my-2 bg-[var(--t-primary)]/5 py-1.5 rounded-r-lg text-[10px]">
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </div>
          ),
          ul: ({ children }) => (
            <ul className="space-y-1 my-1 pl-1">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="space-y-1 my-1 pl-1 list-decimal list-inside">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="flex gap-2 text-[11px] py-0.5 list-none">
              <span className="text-[var(--t-primary)] font-bold text-[8px] mt-1 shrink-0">•</span>
              <span className="flex-1">{searchQuery ? highlightChildren(children, searchQuery) : children}</span>
            </li>
          ),
          p: ({ children }) => (
            <p className="text-[11px] leading-relaxed min-h-[1em] break-words my-0.5">
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-extrabold text-[var(--t-primary)]">
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-[var(--t-primary)]/90">
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </em>
          ),
          code: ({ children }) => (
            <code className="bg-[var(--t-surface-hover)] px-1 rounded text-[10px] font-mono text-[var(--t-primary)]">
              {searchQuery ? highlightChildren(children, searchQuery) : children}
            </code>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
